import type { Action } from '@lucidtech/las-sdk-browser';
import { MarkerType } from '@xyflow/react';
import { useCallback, useMemo } from 'react';

import {
  DefaultNode,
  Edge,
  ExportNode,
  ModelNode,
  NodeType,
  ResourceGroupType,
  TriggerNode,
  ValidationNode,
} from '@/components/flow';
import {
  CREATE_AGENT_RUN_FROM_API_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_N8N_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_POWER_AUTOMATE_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_ZAPIER_FUNCTION_ID,
  CREATE_PREDICTION_FUNCTION_ID,
  CREATE_VALIDATION_TASK_FUNCTION_ID,
  EXPORT_TO_MAKE_FUNCTION_ID,
  EXPORT_TO_MICROSOFT_EXCEL_FUNCTION_ID,
  EXPORT_TO_N8N_FUNCTION_ID,
  EXPORT_TO_POWER_AUTOMATE_FUNCTION_ID,
  EXPORT_TO_WEBHOOK_FUNCTION_ID,
  EXPORT_TO_ZAPIER_FUNCTION_ID,
} from '@/constants';
import { useSuspenseBatchGetActions } from '@/hooks/api';

const calculateXPosition = (n: number, total: number) => {
  const width = 350;
  const offset = (total * width - width) / 2;
  return n * width - offset;
};

const adjustPosition = (nodes: NodeType[]) => {
  const xOffset: Record<ResourceGroupType, number> = {
    trigger: 0,
    model: 0,
    validation: 300,
    export: 0,
    other: 0,
  };
  const yPosition: Record<ResourceGroupType, number> = {
    trigger: 0,
    model: 225,
    validation: 450,
    export: 675,
    other: 900,
  };
  for (const [i, node] of nodes.entries()) {
    node.position.x = xOffset[node.data.resourceGroupType] + calculateXPosition(i, nodes.length);
    node.position.y = yPosition[node.data.resourceGroupType];
  }
};

export type UseNodesAndEdgesOpts = {
  connections: boolean[][];
  resources: string[][];
};

export const useNodesAndEdges = ({ connections, resources }: UseNodesAndEdgesOpts) => {
  const resourceIds = resources.flatMap((resourceId) => resourceId);
  const { data: actions } = useSuspenseBatchGetActions(
    resourceIds.filter((r) => r.startsWith('cradl:action')).map((actionId) => ({ actionId }))
  );

  const getResourceGroupType = useCallback(
    (resourceIds: string[]): { action: Action; type: ResourceGroupType; props: Record<string, string | undefined> } => {
      // TODO: handle errors
      const actionId = resourceIds[0];
      const action = actions.find((a) => a.actionId === actionId) as Action;
      const props: Record<string, string | undefined> = { actionId };
      let type: ResourceGroupType = 'other';

      if (
        [
          CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID,
          CREATE_AGENT_RUN_FROM_POWER_AUTOMATE_FUNCTION_ID,
          CREATE_AGENT_RUN_FROM_ZAPIER_FUNCTION_ID,
          CREATE_AGENT_RUN_FROM_N8N_FUNCTION_ID,
          CREATE_AGENT_RUN_FROM_API_FUNCTION_ID,
        ].includes(action.functionId)
      ) {
        type = 'trigger';
        props.hookId = resourceIds.find((ri) => ri.startsWith('cradl:hook:'));
      } else if (action.functionId === CREATE_PREDICTION_FUNCTION_ID) {
        props.modelId = resourceIds.find((ri) => ri.startsWith('cradl:model:'));
        type = 'model';
      } else if (action.functionId === CREATE_VALIDATION_TASK_FUNCTION_ID) {
        props.validationId = resourceIds.find((ri) => ri.startsWith('cradl:validation:'));
        type = 'validation';
      } else if (
        [
          EXPORT_TO_WEBHOOK_FUNCTION_ID,
          EXPORT_TO_MICROSOFT_EXCEL_FUNCTION_ID,
          EXPORT_TO_ZAPIER_FUNCTION_ID,
          EXPORT_TO_POWER_AUTOMATE_FUNCTION_ID,
          EXPORT_TO_MAKE_FUNCTION_ID,
          EXPORT_TO_N8N_FUNCTION_ID,
        ].includes(action.functionId)
      ) {
        type = 'export';
      }

      return { action, props, type };
    },
    [actions]
  );

  const { nodes, hasExportNode, hasTriggerNode } = useMemo(() => {
    const nodes: NodeType[] = [];
    for (const resourceIds of resources) {
      const { action, type, props } = getResourceGroupType(resourceIds);
      let children = () => <DefaultNode resourceIds={resourceIds} />;
      if (type === 'trigger') {
        children = () => <TriggerNode {...props} />;
      } else if (type === 'model') {
        children = () => <ModelNode {...props} />;
      } else if (type === 'validation') {
        children = () => <ValidationNode {...props} />;
      } else if (type === 'export') {
        children = () => <ExportNode {...props} />;
      }
      nodes.push({
        id: action.actionId,
        position: { x: 0, y: 0 },
        data: { children, resourceGroupType: type, resourceIds },
        type: 'node',
      });
    }

    // TODO: Ugly hack to add empty state, should be handled by a zustand state
    const hasTriggerNode = nodes.find((node) => node.data.resourceGroupType === 'trigger');
    if (!hasTriggerNode) {
      nodes.push({
        id: 'newTrigger',
        position: { x: 0, y: 0 },
        data: {
          children: () => <TriggerNode />,
          resourceGroupType: 'trigger',
          resourceIds: [],
          variant: 'emptyState',
        },
        type: 'node',
      });
    }

    // TODO: Ugly hack to add empty state, should be handled by a zustand state
    const hasExportNode = nodes.find((node) => node.data.resourceGroupType === 'export');
    if (!hasExportNode) {
      nodes.push({
        id: 'newExport',
        position: { x: 0, y: 0 },
        data: {
          children: () => <ExportNode />,
          resourceGroupType: 'export',
          resourceIds: [],
          variant: 'emptyState',
        },
        type: 'node',
      });
    }

    return { nodes, hasTriggerNode, hasExportNode };
  }, [getResourceGroupType, resources]);

  adjustPosition(nodes.filter((n) => n.data.resourceGroupType === 'trigger'));
  adjustPosition(nodes.filter((n) => n.data.resourceGroupType === 'model'));
  adjustPosition(nodes.filter((n) => n.data.resourceGroupType === 'validation'));
  adjustPosition(nodes.filter((n) => n.data.resourceGroupType === 'export'));

  const edges = useMemo(() => {
    const edges: Edge[] = [];
    for (let i = 0; i < resources.length; ++i) {
      for (let j = 0; j < resources.length; ++j) {
        if (connections[i][j]) {
          const fromActionId = resources[i][0];
          const toActionId = resources[j][0];

          const fromAction = actions.find((a) => a.actionId === fromActionId);
          const toAction = actions.find((a) => a.actionId === toActionId);

          let label = undefined;
          let variant: 'warning' | 'success' | undefined = undefined;
          if (fromAction?.functionId === CREATE_PREDICTION_FUNCTION_ID) {
            if (toAction?.functionId === CREATE_VALIDATION_TASK_FUNCTION_ID) {
              label = 'If AI validation failed';
              variant = 'warning';
            } else {
              label = 'If AI validated';
              variant = 'success';
            }
          }

          edges.push({
            id: `${fromActionId}-${toActionId}`,
            source: fromActionId,
            target: toActionId,
            label: label,
            data: { variant },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
            },
            type: 'edge',
          });
        }
      }
    }

    const predictAction = actions.find((a) => a.functionId === CREATE_PREDICTION_FUNCTION_ID);
    const validationAction = actions.find((a) => a.functionId === CREATE_VALIDATION_TASK_FUNCTION_ID);

    if (!hasTriggerNode && predictAction?.actionId) {
      edges.push({
        id: `newTrigger-${predictAction?.actionId}`,
        target: predictAction.actionId,
        source: 'newTrigger',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        type: 'edge',
      });
    }

    if (!hasExportNode && predictAction?.actionId && validationAction?.actionId) {
      edges.push({
        id: `${predictAction?.actionId}-nexExport`,
        source: predictAction.actionId,
        label: 'If AI validated',
        data: { variant: 'success' },
        target: 'newExport',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        type: 'edge',
      });

      edges.push({
        id: `${validationAction?.actionId}-nexExport`,
        source: validationAction!.actionId,
        target: 'newExport',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        type: 'edge',
      });
    }

    return edges;
  }, [actions, connections, hasExportNode, hasTriggerNode, resources]);

  return { nodes, edges };
};
